import { useState } from "react";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";
import { Field, ConditionPicker } from "./FormFields";


const ENDPOINT = "/api/contact.php" 

const EMPTY = {
  name: "", email: "", phone: "", condition: "", make: "", model: "",
  exterior_color: "", interior_color: "", details: "", website: "",
};

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.message || "Something went wrong.");
      setStatus("sent");
      setForm(EMPTY);
    } catch (err) {
      setStatus("idle");
      toast.error("Your request could not be sent", { description: "Please check your details and try again." });
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="border-t border-hairline bg-paper py-24 md:py-36">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 md:px-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Reveal>
            <Eyebrow>Begin your acquisition</Eyebrow>
            <h2 className="mt-6 font-display text-4xl font-light leading-tight tracking-tight md:text-5xl">
              Tell us about the car you want.
            </h2>
            <p className="mt-8 max-w-sm text-base leading-relaxed text-stone">
              Only your name and email are required. Anything else you share about the car helps us start sourcing right away.
              We will reply personally to every request.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="lg:col-span-7 lg:col-start-6">
          {status === "sent" ? (
            <div data-testid="contact-success" className="border-t border-hairline pt-12">
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-ink">
                <Check className="h-5 w-5" strokeWidth={1.25} />
              </span>
              <h3 className="mt-8 font-display text-3xl md:text-4xl">Thank you. Your request has been received.</h3>
              <p className="mt-4 max-w-md text-base leading-relaxed text-stone">Sasha will be in touch personally to discuss the next steps.</p>
              <button type="button" data-testid="contact-send-another" onClick={() => setStatus("idle")} className="link-line mt-10 pb-1 text-sm">
                Send another request
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="space-y-14">
              <fieldset className="grid grid-cols-1 gap-x-10 gap-y-10 md:grid-cols-2">
                <legend className="mb-8 text-[11px] uppercase tracking-[0.28em] text-mist">01 — Your details</legend>
                <Field id="name" label="Full name" required value={form.name} onChange={set("name")} autoComplete="name" />
                <Field id="email" label="Email" type="email" required value={form.email} onChange={set("email")} autoComplete="email" />
                <Field id="phone" label="Phone (optional)" type="tel" value={form.phone} onChange={set("phone")} autoComplete="tel" className="md:col-span-2" />
              </fieldset>
              <fieldset className="grid grid-cols-1 gap-x-10 gap-y-10 md:grid-cols-2">
                <legend className="mb-8 text-[11px] uppercase tracking-[0.28em] text-mist">02 — The car (optional)</legend>
                <ConditionPicker value={form.condition} onChange={(v) => setForm((f) => ({ ...f, condition: v }))} />
                <Field id="make" label="Make" placeholder="e.g. Porsche" value={form.make} onChange={set("make")} />
                <Field id="model" label="Model" placeholder="e.g. 911 Carrera S" value={form.model} onChange={set("model")} />
                <Field id="exterior_color" label="Exterior colour" value={form.exterior_color} onChange={set("exterior_color")} />
                <Field id="interior_color" label="Interior colour" value={form.interior_color} onChange={set("interior_color")} />
                <Field id="details" label="Wants, needs & deal breakers" multiline placeholder="Options, mileage, budget, timeline, anything you will not accept…" value={form.details} onChange={set("details")} className="md:col-span-2" />
              </fieldset>
              <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden />
              <button type="submit" disabled={status === "sending"} data-testid="contact-submit-button" className="group inline-flex items-center gap-3 bg-ink px-10 py-5 text-xs uppercase tracking-[0.22em] text-ivory hover:bg-[#1a1a1a] transition-colors duration-500 disabled:opacity-60">
                {status === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {status === "sending" ? "Sending" : "Send my request"}
                {status !== "sending" && <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.25} />}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
